<template>
  <div
    class="flex items-end gap-2"
    :class="
      isBoxed
        ? 'rounded-md border border-obsidian-200 bg-white p-1.5 transition-colors focus-within:border-obsidian-400'
        : ''
    "
  >
    <div class="relative min-w-0 flex-1">
      <p
        v-if="isEditable && empty"
        class="pointer-events-none absolute inset-0 z-0 text-sm text-obsidian-400"
        :class="contentPadding"
      >
        {{ placeholder }}
      </p>
      <EditorContent
        v-if="editor"
        :editor="editor"
        class="comment-editor relative z-[1] text-sm"
        :class="[contentPadding, { 'comment-editor--editing': isEditable }]"
      />
    </div>
    <CVButton
      v-if="showCancel && isEditable"
      type="button"
      variant="ghost"
      size="iconXs"
      class="shrink-0 text-obsidian-400 hover:text-obsidian-800"
      :disabled="isSubmitting"
      :aria-label="cancelLabel"
      :title="cancelLabel"
      @click="emit('cancel')"
    >
      <XIcon class="size-4" />
    </CVButton>
    <CVButton
      v-if="showSubmit && isEditable"
      type="button"
      variant="default"
      size="iconXs"
      class="shrink-0 disabled:opacity-30"
      :disabled="empty || isSubmitting"
      :aria-label="submitLabel"
      :title="submitLabel"
      @click="emit('submit')"
    >
      <component :is="submitIcon ?? SendIcon" class="size-4" />
    </CVButton>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch, type Component } from 'vue';
import { useRouter } from 'vue-router';
import { Editor, EditorContent } from '@tiptap/vue-3';
import { SuggestionPluginKey } from '@tiptap/suggestion';
import { SendIcon, XIcon } from 'lucide-vue-next';
import CVButton from '@/components/buttons/CVButton.vue';
import {
  createCommentExtensions,
  emojiSuggestionKey,
  entityLinkSuggestionKey,
} from './comment-editor-extensions';
import type { CommentableEntityType, TipTapNode } from '@/api/comments.service';

const router = useRouter();

const props = withDefaults(
  defineProps<{
    autofocus?: boolean;
    bare?: boolean;
    entityId: string;
    entityType: CommentableEntityType;
    modelValue?: TipTapNode | null;
    editable?: boolean;
    placeholder: string;
    noMatches: string;
    showSubmit?: boolean;
    isSubmitting?: boolean;
    submitLabel?: string;
    submitIcon?: Component;
    showCancel?: boolean;
    cancelLabel?: string;
  }>(),
  {
    autofocus: false,
    bare: false,
    editable: true,
    showSubmit: false,
    isSubmitting: false,
    submitIcon: undefined,
    showCancel: false,
    cancelLabel: undefined,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: TipTapNode];
  submit: [];
  cancel: [];
}>();

const isEditable = computed(() => props.editable);
const isBoxed = computed(() => isEditable.value && !props.bare);
const contentPadding = computed(() => (isBoxed.value ? 'px-1.5 py-1' : ''));
const empty = ref(true);
const editor = shallowRef<Editor | null>(null);

onMounted(() => {
  try {
    editor.value = new Editor({
      editable: isEditable.value,
      autofocus: props.autofocus && isEditable.value ? 'end' : false,
      content: props.modelValue ?? undefined,
      extensions: createCommentExtensions({
        entityType: props.entityType,
        entityId: props.entityId,
        noMatches: props.noMatches,
        editable: isEditable.value,
      }),
      editorProps: {
        handleKeyDown: (view, event) => {
          if (!props.showSubmit || event.key !== 'Enter') return false;
          if (
            SuggestionPluginKey.getState(view.state)?.active ||
            entityLinkSuggestionKey.getState(view.state)?.active ||
            emojiSuggestionKey.getState(view.state)?.active
          ) {
            return false;
          }
          if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
            return editor.value?.commands.setHardBreak() ?? false;
          }
          if (empty.value || props.isSubmitting) return true;
          emit('submit');
          return true;
        },
        handleClick: (_view, _pos, event) => {
          const target = (event.target as HTMLElement | null)?.closest?.('a');
          if (!target) return false;
          if (isEditable.value) {
            event.preventDefault();
            return true;
          }
          if (!target.classList.contains('entity-link')) return false;
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return false;
          }
          event.preventDefault();
          const href = target.getAttribute('href');
          if (href?.startsWith('/')) void router.push(href);
          return true;
        },
      },
      onUpdate: ({ editor: instance }) => {
        empty.value = instance.isEmpty;
        emit('update:modelValue', instance.getJSON() as TipTapNode);
      },
    });
    empty.value = editor.value.isEmpty;
  } catch (error) {
    console.error('Failed to create comment editor', error);
  }
});

onBeforeUnmount(() => {
  editor.value?.destroy();
  editor.value = null;
});

watch(
  () => props.modelValue,
  (value) => {
    if (!editor.value) return;
    empty.value = editor.value.isEmpty;
    if (!value) return;
    const current = JSON.stringify(editor.value.getJSON());
    if (current === JSON.stringify(value)) return;
    editor.value.commands.setContent(value);
    empty.value = editor.value.isEmpty;
  },
  { immediate: true },
);

const isEmpty = () => editor.value?.isEmpty ?? true;
const getJSON = () => editor.value?.getJSON() as TipTapNode;
const clear = () => {
  editor.value?.commands.clearContent();
  empty.value = true;
};
const setContent = (value: TipTapNode) => {
  editor.value?.commands.setContent(value);
  empty.value = editor.value?.isEmpty ?? true;
};

defineExpose({ isEmpty, getJSON, clear, setContent });
</script>

<style>
.comment-editor :is(.tiptap, .ProseMirror) {
  @apply min-h-6 outline-none;
}
.comment-editor :is(.tiptap, .ProseMirror) p.is-editor-empty:first-child::before,
.comment-editor :is(.tiptap, .ProseMirror) p.is-empty:first-child::before {
  content: none;
}
.comment-editor :is(.tiptap, .ProseMirror) > * + * {
  @apply mt-1.5;
}
.comment-editor :is(.tiptap, .ProseMirror) ul {
  @apply list-disc pl-5;
}
.comment-editor :is(.tiptap, .ProseMirror) ol {
  @apply list-decimal pl-5;
}
.comment-editor :is(.tiptap, .ProseMirror) a {
  @apply text-hazel-700 underline underline-offset-2;
}
.comment-editor .mention {
  @apply whitespace-nowrap rounded bg-hazel-100 px-1 py-px font-medium text-hazel-900;
}
.comment-editor .entity-link {
  @apply whitespace-nowrap;
}
.comment-editor .entity-link[data-entity-type='car'] {
  @apply no-underline;
}
.comment-editor .custom-emoji {
  @apply mx-0.5 inline-block size-[1.25em] object-contain align-[-0.2em];
}
.comment-editor--editing a {
  @apply pointer-events-none;
}
</style>
